/**
 * OAuth success landing page.
 *
 * The platform OAuth callback (/api/platform/auth/oauth/<provider>/callback)
 * redirects here with the freshly-minted platform JWT in the URL query:
 *
 *   /auth/oauth-success?token=<jwt>&email=<email>&tenant_slug=<slug>&return_to=<path>
 *
 * This page extracts the token, persists it the same way the password
 * login flow does (auth_token + user in localStorage), and bounces to the
 * platform home. Mirrors completeLoginAndRedirect() in src/app/login/page.tsx.
 *
 * Triveny-only — WeaveIn doesn't ship OAuth login today. If a non-Triveny
 * visitor lands here we bounce them to /login to avoid silent token reuse.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { config } from '@/src/config';
import { tvTokens, TvCard, TvLabel } from '@/src/platform/shared/components/triveny';

export default function OAuthSuccessPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    if (!config.isTriveny) {
      navigate('/login', { replace: true });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const email = params.get('email');
    const tenantSlug = params.get('tenant_slug');
    const returnTo = params.get('return_to') || '/';
    if (!token || !email) {
      setReason('Missing token in callback');
      setStatus('error');
      return;
    }
    try {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('session_start', Date.now().toString());
      // The OAuth response doesn't include a full user dict (we minted the
      // JWT directly without going through the password-login response
      // shape). Synthesize a minimal one — the platform JWT already
      // carries the canonical claims; downstream UI reads from there.
      localStorage.setItem(
        'user',
        JSON.stringify({ email, tenant_slug: tenantSlug, mfa_enabled: true }),
      );
      // OAuth users skip 2FA enrollment — the IdP enforces its own MFA.
      window.location.replace(returnTo);
    } catch (e: any) {
      setReason(e?.message || 'Failed to store session');
      setStatus('error');
    }
  }, [navigate]);

  return (
    <div
      className="triveny-platform"
      style={{ minHeight: '100vh', background: tvTokens.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div style={{ maxWidth: 420, width: '100%', padding: 22 }}>
        <TvCard bodyPadding={24}>
          {status === 'processing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle2 size={18} color={tvTokens.successInk} />
              <TvLabel>Signing you in…</TvLabel>
            </div>
          )}
          {status === 'error' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <AlertCircle size={18} color={tvTokens.danger} />
                <TvLabel>Sign-in failed</TvLabel>
              </div>
              <p style={{ fontSize: 13, color: tvTokens.ink500, margin: 0 }}>{reason}</p>
              <p style={{ fontSize: 12, color: tvTokens.ink500, marginTop: 14 }}>
                <a href="/login" style={{ color: tvTokens.accentInk }}>
                  Return to sign-in
                </a>
              </p>
            </>
          )}
        </TvCard>
      </div>
    </div>
  );
}
